<template>
  <f7-page>
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>

    <div v-if="loaded" class="custom-table-container tableContainer">
      <EnvironmentSamplesTable :store="customStore" />
    </div>
  </f7-page>
</template>

<script>
import EnvironmentSamplesTable from '@/components/Environment/SamplesTable/index.vue';
import { mapActions } from 'vuex';
import EventBus from '@/services/event-bus';
import { NOTIFY_TIME_LONG, ATCS_ES } from '@/js/constants';

export default {
  name: 'EnvironmentSamplesPage',
  components: { EnvironmentSamplesTable },
  data() {
    return {
      loaded: false,
      customStore: null,
      environmentSamplesHandler: null,
    };
  },
  beforeMount() {
    this.setCurrentRoute([
      {
        name: this.$t('Environment'),
        disabled: false,
        page: 'EnvironmentSamplesPage',
        active: false,
      },
      {
        name: this.$t('agronomySample.exportExcelSampleTable'),
        disabled: true,
        page: 'EnvironmentSamplesPage',
        active: true,
      },
    ]);

    this.setCurrentFilter('environment');

    const isAlert = this.$f7route?.query?.dashboardAlert === 'true';
    const isInProcess =
      this.$f7route?.query?.dashboardInProcess === 'true';
    const isNotReviewed =
      this.$f7route?.query?.dashboardNotReviewed === 'true';

    if (isAlert || isInProcess || isNotReviewed) {
      const params = {
        receptionDate_after: '',
        receptionDate_before: '',
        endDate_after: '',
        endDate_before: '',
        sample_type: '',
        analyse_type: '',
        atc_name: ATCS_ES.environment,
        summary_semaphore: '',
        status: '',
        not_seen: '',
      };

      if (isAlert) params.summary_semaphore = 'red,orange';
      if (isInProcess) params.status = '2';
      if (isNotReviewed) params.not_seen = true;

      // ✅ últimos 6 meses
      const endDate = new Date();
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 6);

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      params.receptionDate_after = formatDate(startDate);
      params.receptionDate_before = formatDate(endDate);

      this.setAllFilterParams(params);
    }
  },
  async mounted() {
    try {
      this.loaded = false;

      const isAlert = this.$f7route?.query?.dashboardAlert === 'true';
      const isInProcess =
        this.$f7route?.query?.dashboardInProcess === 'true';
      const isNotReviewed =
        this.$f7route?.query?.dashboardNotReviewed === 'true';

      const fromDashboard = isAlert || isInProcess || isNotReviewed;
      let hasQuickFilters = false;
      const raw = localStorage.getItem('environmentSamplesTable');
      if (raw) {
        try {
          const saved = JSON.parse(raw);
          const f = saved.isFilterActive || {};
          hasQuickFilters =
            f.inProcess ||
            f.finished ||
            f.cancelled ||
            f.lastMonth ||
            f.noReviewed ||
            f.onAlert ||
            f.noPositives;
        } catch (e) {
          console.warn(
            'foodSamplesTable corrupto en localStorage',
            e
          );
        }
      }

      if (fromDashboard) {
        const params = {
          receptionDate_after: '',
          receptionDate_before: '',
          endDate_after: '',
          endDate_before: '',
          sample_type: '',
          analyse_type: '',
          atc_name: ATCS_ES.environment,
          summary_semaphore: '',
          status: '',
          not_seen: '',
        };

        if (isAlert) params.summary_semaphore = 'red,orange';
        if (isInProcess) params.status = '2';
        if (isNotReviewed) params.not_seen = true;

        const { receptionDate_after, receptionDate_before } =
          this.getLastMonthsDates(6);
        params.receptionDate_after = receptionDate_after;
        params.receptionDate_before = receptionDate_before;

        this.setAllFilterParams(params);
        this.customStore = await this.getStoreWithFilters(params);
      } else if (!hasQuickFilters) {
        setTimeout(() => {
          this.applyCurrentFilters();
        }, 0);
      }

      this.environmentSamplesHandler = async (params) => {
        if (params)
          this.customStore = await this.getStoreWithFilters(params);
        else this.customStore = await this.getStore();
      };
      EventBus.$on('EnvironmentSamplesPage', this.environmentSamplesHandler);
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error',
        NOTIFY_TIME_LONG
      );
    } finally {
      this.loaded = true;
    }
  },
  beforeDestroy() {
    if (this.environmentSamplesHandler) {
      EventBus.$off('EnvironmentSamplesPage', this.environmentSamplesHandler);
    }
  },
  methods: {
    getLastMonthsDates(months) {
      const endDate = new Date();
      const startDate = new Date(endDate);

      const originalDay = startDate.getDate();
      const targetMonth = startDate.getMonth() - months;

      startDate.setMonth(targetMonth);

      // Si el mes no tiene ese día (ej: 31), JS desborda al mes siguiente.
      // Lo corregimos llevándolo al último día del mes correcto.
      if (startDate.getDate() !== originalDay) {
        startDate.setDate(0);
      }

      const formatDate = (date) => {
        const d = new Date(date);
        let month = '' + (d.getMonth() + 1);
        let day = '' + d.getDate();
        const year = d.getFullYear();
        if (month.length < 2) month = '0' + month;
        if (day.length < 2) day = '0' + day;
        return [year, month, day].join('-');
      };

      return {
        receptionDate_after: formatDate(startDate),
        receptionDate_before: formatDate(endDate),
      };
    },

    ...mapActions('environmentSamples', [
      'getStore',
      'getStoreWithFilters',
    ]),
    ...mapActions('breadcrumb', ['setCurrentRoute']),
    ...mapActions('filter', [
      'setCurrentFilter',
      'applyCurrentFilters',
    ]),
    ...mapActions('samples', ['setAllFilterParams']),
  },
};
</script>
